/**
 * Payment Proof Management Page
 * Admin page for reviewing and approving payment proofs
 */

import React, { useState } from "react";
import Button from "../../components/ui/Button";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import { useToast } from "../../contexts/ToastContext";
import {
	useListPaymentProofsQuery,
	useUpdatePaymentProofMutation,
	type PaymentProof,
} from "../../store/api/paymentProofsApi";
import PaymentProofDetailModal from "./components/PaymentProofDetailModal";
import PaymentProofFilters from "./components/PaymentProofFilters";
import PaymentProofTable from "./components/PaymentProofTable";

const PaymentProofManagement: React.FC = () => {
	const { success, error: showError } = useToast();
	const [page, setPage] = useState(1);
	const [filters, setFilters] = useState({
		status: "",
		search: "",
	});
	const [selectedProof, setSelectedProof] = useState<PaymentProof | null>(null);
	const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
	const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
	const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
	const [proofToAction, setProofToAction] = useState<PaymentProof | null>(null);
	const [reviewNotes, setReviewNotes] = useState("");

	const {
		data: proofsData,
		isLoading,
		error,
		refetch,
	} = useListPaymentProofsQuery({
		page,
		page_size: 20,
		status: filters.status || undefined,
		search: filters.search || undefined,
		ordering: "-created_at",
	});

	const [updatePaymentProof, { isLoading: isUpdating }] =
		useUpdatePaymentProofMutation();

	const handleFilterChange = (newFilters: {
		status?: string;
		search?: string;
	}) => {
		setFilters((prev) => ({ ...prev, ...newFilters }));
		setPage(1);
	};

	const handleProofClick = (proof: PaymentProof) => {
		setSelectedProof(proof);
		setIsDetailModalOpen(true);
	};

	const handleApproveClick = (proof: PaymentProof) => {
		setProofToAction(proof);
		setReviewNotes("");
		setIsApproveModalOpen(true);
	};

	const handleRejectClick = (proof: PaymentProof) => {
		setProofToAction(proof);
		setReviewNotes("");
		setIsRejectModalOpen(true);
	};

	const handleConfirmApprove = async () => {
		if (!proofToAction) return;

		try {
			await updatePaymentProof({
				id: proofToAction.id,
				data: {
					status: "approved",
					review_notes: reviewNotes || undefined,
				},
			}).unwrap();

			success("Payment proof approved successfully!");
			setIsApproveModalOpen(false);
			setProofToAction(null);
			setReviewNotes("");
			refetch();
			if (selectedProof?.id === proofToAction.id) {
				setIsDetailModalOpen(false);
			}
		} catch (err: any) {
			showError(
				err?.data?.detail ||
					"Failed to approve payment proof. Please try again.",
			);
		}
	};

	const handleConfirmReject = async () => {
		if (!proofToAction) return;

		try {
			await updatePaymentProof({
				id: proofToAction.id,
				data: {
					status: "rejected",
					review_notes: reviewNotes || undefined,
				},
			}).unwrap();

			success("Payment proof rejected successfully!");
			setIsRejectModalOpen(false);
			setProofToAction(null);
			setReviewNotes("");
			refetch();
			if (selectedProof?.id === proofToAction.id) {
				setIsDetailModalOpen(false);
			}
		} catch (err: any) {
			showError(
				err?.data?.detail ||
					"Failed to reject payment proof. Please try again.",
			);
		}
	};

	return (
		<div className="w-full">
			<div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
				{/* Page Header */}
				<div className="mb-6">
					<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
						<div>
							<h1 className="text-2xl sm:text-3xl font-bold text-foreground">
								Payment Management
							</h1>
							<p className="text-muted-foreground mt-1">
								Review and approve payment proofs submitted by tenants and
								buyers
							</p>
						</div>
					</div>
				</div>

				{/* Filters */}
				<PaymentProofFilters
					filters={filters}
					onFilterChange={handleFilterChange}
				/>

				{/* Proofs Table */}
				<div className="mt-6">
					<PaymentProofTable
						proofs={proofsData?.results || []}
						isLoading={isLoading}
						onProofClick={handleProofClick}
						onApprove={handleApproveClick}
						onReject={handleRejectClick}
					/>
				</div>

				{/* Pagination */}
				{proofsData && proofsData.count > 0 && (
					<div className="mt-6 flex items-center justify-between">
						<p className="text-sm text-muted-foreground">
							Showing {(page - 1) * 20 + 1} to{" "}
							{Math.min(page * 20, proofsData.count)} of {proofsData.count}{" "}
							proofs
						</p>
						<div className="flex items-center space-x-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={page === 1 || isLoading}
							>
								Previous
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setPage((p) => p + 1)}
								disabled={
									!proofsData.next || isLoading || page * 20 >= proofsData.count
								}
							>
								Next
							</Button>
						</div>
					</div>
				)}

				{/* Detail Modal */}
				{selectedProof && (
					<PaymentProofDetailModal
						proof={selectedProof}
						isOpen={isDetailModalOpen}
						onClose={() => {
							setIsDetailModalOpen(false);
							setSelectedProof(null);
						}}
						onApprove={() => handleApproveClick(selectedProof)}
						onReject={() => handleRejectClick(selectedProof)}
					/>
				)}

				{/* Approve Confirmation Modal */}
				<ConfirmationModal
					isOpen={isApproveModalOpen}
					onClose={() => {
						setIsApproveModalOpen(false);
						setProofToAction(null);
						setReviewNotes("");
					}}
					onConfirm={handleConfirmApprove}
					title="Approve Payment Proof"
					message={
						<div className="space-y-4">
							<p>Are you sure you want to approve this payment proof?</p>
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Review Notes (Optional)
								</label>
								<textarea
									value={reviewNotes}
									onChange={(e) => setReviewNotes(e.target.value)}
									className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
									rows={3}
									placeholder="Add any notes about this approval..."
								/>
							</div>
						</div>
					}
					confirmButtonText="Approve"
					isLoading={isUpdating}
				/>

				{/* Reject Confirmation Modal */}
				<ConfirmationModal
					isOpen={isRejectModalOpen}
					onClose={() => {
						setIsRejectModalOpen(false);
						setProofToAction(null);
						setReviewNotes("");
					}}
					onConfirm={handleConfirmReject}
					title="Reject Payment Proof"
					message={
						<div className="space-y-4">
							<p>Are you sure you want to reject this payment proof?</p>
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Rejection Reason (Required)
								</label>
								<textarea
									value={reviewNotes}
									onChange={(e) => setReviewNotes(e.target.value)}
									className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
									rows={3}
									placeholder="Please provide a reason for rejection..."
									required
								/>
							</div>
						</div>
					}
					confirmButtonText="Reject"
					isDestructive={true}
					isLoading={isUpdating}
				/>
			</div>
		</div>
	);
};

export default PaymentProofManagement;
